/** @jsxImportSource @emotion/react */

import { RadioGroup } from '@app/components/Atoms/RadioGroup'
import {
  DataPoint,
  ProjectedRiskChart,
} from '@app/components/Molecules/Charts/ProjectedRiskChart/ProjectedRiskChart'
import { roundForDisplay } from '@app/components/Molecules/Charts/utils/utils'
import { RegionOption } from '@app/components/Molecules/RegionFilter'
import { EmptySection } from '@app/components/Molecules/RiskSideDrawerContent/components/EmptySection'
import {
  layerDataByHazardIdDetail,
  layerDataHazardIdTransformer,
  parameterKeyTitleConverter,
  useLayerDataFetcher,
} from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { hazardBoxSLRSlider } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/HazardControls/HazardControls.styles'
import { Slider } from '@app/components/Molecules/Slider'
import apiClient from '@app/config/axios.config'
import {
  InterpretPanel,
  InterpretToggle,
} from '@app/modules/Risk/Profile/components/InterpretToggle'
import { projectedRiskChartStateAtom } from '@app/risk-migration/atoms/projectedRiskChart.atoms'
import { drawAreasAtom, regionMasksAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import type { DrawArea, MapLayer } from '@app/types/layerTypes'
import { convertToMetersOrCentimeters } from '@app/utils/numbers.utils'
import { Circle, ErrorOutline, InfoOutlined } from '@mui/icons-material'
import {
  Box,
  css,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { projectedRiskChartDataAtom } from '@app/state/projectedRiskChartDataAtom'
import { Polygon } from 'geojson'
import { useAtomValue, useSetAtom } from 'jotai'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { chartContainer, chartHeader, chartSection } from '../RiskContent.styles'
import { radioGroup, radioGroupContent, toggleGroup } from './ProjectedRisk.styles'

interface ProjectedRiskProps {
  assetLayer: MapLayer
  hazardLayer: MapLayer
  yAxisTitle: string
  identifyingInformationKey?: string
  onlyShowTotal?: boolean
}
type VulnerabilityLevel =
  | 'unspecified'
  | 'potential'
  | 'exposed'
  | 'insignificant'
  | 'low'
  | 'minor'
  | 'moderate'
  | 'major'
  | 'high'

type ProjectedRiskSecondaryParameter = {
  key: string
  value: string | number
  availableValues: (number | string)[]
}

// Response shape of GET /asset/expected_annual_consequence/
type ExpectedAnnualConsequenceDatum = {
  eac: number
  assets_missing_attributes: { total_missing_attributes: number }
  asset_counts: { total_count: number }
} & Record<string, unknown>

export const VULNERABILITY_PRIORITY_ORDER = {
  unspecified: 0,
  potential: 1,
  exposed: 2,
  insignificant: 3,
  low: 4,
  minor: 5,
  moderate: 6,
  major: 7,
  high: 8,
}

type FormatValueFn = (key: string | undefined, value: number | string | null | undefined) => string

// This likely goes against React's philosophy of dealing with state but after fighting
// many weird useEffect cascades I certify this as: good-enough.
// Fetches and manages one parameter dimension's projected-risk series (primaryParameterKey held
// variable, secondaryParameterKey held constant at a user-selectable value). Called once per
// dimension so both can be shown at once, independently of each other.
//
// mode 'eac' switches to the /asset/expected_annual_consequence GET endpoint
function useProjectedRiskSeries({
  primaryParameterKey,
  secondaryParameterKey,
  hazardGroupedByHazardId,
  hazardLayer,
  assetLayer,
  regionMasks,
  drawAreas,
  identifyingInformationKey,
  onlyShowTotal = false,
  mode = 'ari',
}: {
  primaryParameterKey: string | undefined
  secondaryParameterKey: string | undefined
  hazardGroupedByHazardId: layerDataByHazardIdDetail | undefined
  hazardLayer: MapLayer
  assetLayer: MapLayer
  regionMasks: RegionOption[] | null
  drawAreas: DrawArea[]
  identifyingInformationKey?: string
  onlyShowTotal?: boolean
  mode?: 'ari' | 'eac'
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [projectedRiskData, setProjectedRiskData] = useState<DataPoint[]>([])
  const [secondaryParameter, setSecondaryParameter] = useState<
    ProjectedRiskSecondaryParameter | undefined
  >(undefined)

  const hasMissingData = useMemo(() => {
    return projectedRiskData.some((dataPoint) => {
      if (!dataPoint.missing_assets) return false
      return Object.values(dataPoint.missing_assets).some((value) => value > 0)
    })
  }, [projectedRiskData])

  const vulnerabilityList = useMemo(() => {
    const listedVulns = onlyShowTotal
      ? ['total']
      : [...new Set(projectedRiskData.flatMap((dataPoint) => Object.keys(dataPoint)))].filter(
          (vuln) => vuln == 'total' || Object.keys(VULNERABILITY_PRIORITY_ORDER).includes(vuln),
        )
    return listedVulns.sort((a: string, b: string) => {
      return (
        (VULNERABILITY_PRIORITY_ORDER[a as VulnerabilityLevel] ?? 0) -
        (VULNERABILITY_PRIORITY_ORDER[b as VulnerabilityLevel] ?? 0)
      )
    })
  }, [onlyShowTotal, projectedRiskData])

  const parameterUnit = primaryParameterKey
    ? `${
        (hazardGroupedByHazardId?.parameters[primaryParameterKey]?.unit ?? '').trim()
          ? ` (${hazardGroupedByHazardId?.parameters[primaryParameterKey]?.unit})`
          : ''
      }`
    : ''

  const getProjectedRiskData = useCallback(
    async () => {
      if (!hazardGroupedByHazardId || !primaryParameterKey) {
        setIsLoading(false)
        setIsFirstLoad(false)
        return
      }

      setIsLoading(true)
      setHasError(false)

      const regions = regionMasks ?? []
      const regionMap = regions.map((region) => region.region)
      const regionsQueryString = regionMap.length > 0 ? `?regions=${regionMap.join(',')}` : ''
      const drawAreaCoordinateArray: Polygon | null =
        drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
      const drawAreaCsv = drawAreaCoordinateArray
        ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
        : ''
      const drawAreaCsvQueryString = drawAreaCsv
        ? `${regionsQueryString ? '&' : '?'}draw_area=${drawAreaCsv}`
        : ''

      const filters = {
        ...hazardLayer.parameters,
      }

      // As we want all possible values for the primary parameter this gets removed from the filters
      delete filters[primaryParameterKey]

      if (secondaryParameter && secondaryParameter.key !== null)
        filters[secondaryParameter.key] = secondaryParameter.value

      if (mode === 'eac') {
        const url = `/asset/expected_annual_consequence/${
          identifyingInformationKey ?? 'hazard_scenario_metric'
        }/${
          assetLayer.type
        }/${hazardLayer.hazard_id}/${primaryParameterKey}${regionsQueryString}${drawAreaCsvQueryString}`

        try {
          const res = await apiClient.post(url, filters)

          return (res.data as ExpectedAnnualConsequenceDatum[]).map((datum) => ({
            [primaryParameterKey]: datum[primaryParameterKey],
            total: datum.eac,
            missing_assets: { total: datum.assets_missing_attributes.total_missing_attributes },
            asset_counts: { total: datum.asset_counts.total_count },
          }))
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`Error fetching expected annual consequence data: ${e}`)
          setHasError(true)
        } finally {
          setIsLoading(false)
          setIsFirstLoad(false)
        }
        return
      }

      const url = `/asset/projected_risk/${identifyingInformationKey ?? 'hazard_scenario_metric'}/${
        assetLayer.type
      }/${hazardLayer.hazard_id}/${primaryParameterKey}${regionsQueryString}${drawAreaCsvQueryString}`

      try {
        const res = await apiClient.post(url, filters)

        return res.data
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`Error fetching projected risk data: ${e}`)
        setHasError(true)
      } finally {
        setIsLoading(false)
        setIsFirstLoad(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      primaryParameterKey,
      regionMasks,
      drawAreas,
      secondaryParameter?.value,
      assetLayer.type,
      hazardLayer.hazard_id,
      identifyingInformationKey,
      mode,
    ],
  )

  const handleSecondaryParameterChange = (newSecondaryParameterValue: number) => {
    setIsLoading(true)

    setSecondaryParameter((old) => {
      if (!old) return old
      return { ...old, value: newSecondaryParameterValue }
    })
  }

  useEffect(() => {
    getProjectedRiskData().then((data: DataPoint[]) => {
      if (!data) return

      // Remove all vulnerabilities that are zero for entire projection
      const vulnerabilityTotals: { [vulnerability: string]: number } = {}

      data.forEach((datum) => {
        Object.entries(datum).forEach(([vulnerability, value]) => {
          if (typeof value !== 'number') return

          if (Object.hasOwn(vulnerabilityTotals, vulnerability))
            vulnerabilityTotals[vulnerability] += value
          else vulnerabilityTotals[vulnerability] = value
        })
      })

      Object.entries(vulnerabilityTotals).forEach(([vulnerability, total]) => {
        if (!total) {
          data.forEach((datum) => {
            delete datum[vulnerability]
          })
        }
      })

      setProjectedRiskData(data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    primaryParameterKey,
    secondaryParameter?.value,
    drawAreas,
    regionMasks,
    identifyingInformationKey,
    mode,
  ])

  useEffect(() => {
    if (
      secondaryParameter ||
      !hazardGroupedByHazardId ||
      !primaryParameterKey ||
      !secondaryParameterKey
    )
      return

    const primaryParameterValue =
      hazardLayer.parameters && hazardLayer.parameters[primaryParameterKey]

    const parametersToFilter = {
      ...hazardLayer.parameters,
      [primaryParameterKey]: primaryParameterValue,
    }

    delete parametersToFilter[secondaryParameterKey]

    const secondaryParameterAvailableValues = [
      ...new Set(
        hazardGroupedByHazardId.scenarios
          .filter((scenario) => {
            for (const skey in scenario.parameters) {
              if (skey == secondaryParameterKey) continue
              if (scenario.parameters[skey] !== parametersToFilter[skey]) return false
            }
            return true
          })
          .map((scenario) => scenario.parameters[secondaryParameterKey]),
      ),
    ].map((value) => parseFloat(`${value}`))

    setSecondaryParameter({
      key: secondaryParameterKey,
      value: secondaryParameterAvailableValues[0],
      availableValues: secondaryParameterAvailableValues,
    })
  }, [
    hazardGroupedByHazardId,
    hazardLayer.parameters,
    primaryParameterKey,
    secondaryParameterKey,
    secondaryParameter,
  ])

  const secondaryParameterFormInput = hazardGroupedByHazardId?.form.find(
    (input) => input.key == secondaryParameter?.key,
  )

  return {
    primaryParameterKey,
    secondaryParameter,
    secondaryParameterFormInput,
    handleSecondaryParameterChange,
    projectedRiskData,
    isLoading,
    isFirstLoad,
    hasError,
    vulnerabilityList,
    hasMissingData,
    parameterUnit,
  }
}

type ProjectedRiskSeries = ReturnType<typeof useProjectedRiskSeries>

// Shared rendering for one series block (secondary-parameter control, chart, missing-data notice,
// empty state). `headerExtra`/`modeExtra`/`belowHeaderExtra` let the Risk Curves block inject its
// View toggle, mode-specific title/info, and EAL-replacement stat without duplicating the rest of
// the layout for the Projected Risk block.
function ProjectedRiskSeriesBlock({
  theme,
  title,
  titleDescription,
  titleInterpret,
  headerExtra,
  modeExtra,
  belowHeaderExtra,
  isRiskCurvesBlock,
  series,
  hazardGroupedByHazardId,
  assetLayer,
  yAxisTitle,
  xAxisMode,
  identifyingInformationKey,
  formatValue,
  hideSecondaryParameterControl,
}: {
  theme: Theme
  title: string
  titleDescription?: string
  titleInterpret?: ReactNode
  headerExtra?: ReactNode
  modeExtra?: ReactNode
  belowHeaderExtra?: ReactNode
  isRiskCurvesBlock: boolean
  series: ProjectedRiskSeries
  hazardGroupedByHazardId: layerDataByHazardIdDetail | undefined
  assetLayer: MapLayer
  yAxisTitle: string
  xAxisMode: 'ari' | 'aep'
  identifyingInformationKey?: string
  formatValue: FormatValueFn
  hideSecondaryParameterControl?: boolean
}) {
  const [showTitleInterpret, setShowTitleInterpret] = useState(false)

  if (series.isFirstLoad) {
    return <EmptySection isLoading={series.isLoading} title="Loading..." />
  }

  return (
    <>
      <Box
        css={[
          chartHeader({ theme }),
          css`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding-right: 32px;
          `,
        ]}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
          <Typography>{title}</Typography>
          {titleDescription && (
            <Tooltip title={titleDescription} placement="right">
              <InfoOutlined
                sx={{ fontSize: '16px', color: '#0b2948', opacity: 0.6, cursor: 'help' }}
              />
            </Tooltip>
          )}
        </Box>
        {titleInterpret && (
          <InterpretToggle
            active={showTitleInterpret}
            onClick={() => setShowTitleInterpret((v) => !v)}
          />
        )}
      </Box>
      {titleInterpret && showTitleInterpret && (
        <Box sx={{ paddingLeft: '48px', paddingRight: '32px', marginTop: '8px' }}>
          <InterpretPanel>{titleInterpret}</InterpretPanel>
        </Box>
      )}
      {headerExtra}
      {modeExtra}

      {series.secondaryParameter && !hideSecondaryParameterControl && (
        <Box
          sx={{
            paddingLeft: '32px',
            paddingRight: '32px',
            paddingTop: '4px',
          }}
        >
          <Box
            css={[
              radioGroup({ theme }),
              css`
                flex-grow: 1;
                width: 100%;
                flex-direction: column;
                align-items: flex-start;
              `,
            ]}
          >
            <Typography sx={{ marginRight: theme.spacing(6), fontSize: '12px', color: '#0b2948' }}>
              {series.secondaryParameterFormInput?.title}
              {isRiskCurvesBlock && (
                <Box component="span" sx={{ fontWeight: 400, opacity: 0.6, marginLeft: '4px' }}>
                  scenario
                </Box>
              )}
            </Typography>
            {series.secondaryParameter.availableValues?.length > 1 && (
              <>
                {(series.secondaryParameterFormInput?.type == 'slider' ||
                  series.secondaryParameterFormInput?.type == 'sealevelSlider') && (
                  <Box css={hazardBoxSLRSlider({ theme })}>
                    <Slider
                      key={`${series.secondaryParameterFormInput.key}-slider`}
                      value={parseFloat(`${series.secondaryParameter.value ?? 0}`)}
                      handleValueChange={series.handleSecondaryParameterChange}
                      marks={series.secondaryParameter.availableValues.map((value) => {
                        const parameter =
                          series.secondaryParameterFormInput &&
                          hazardGroupedByHazardId?.parameters[
                            series.secondaryParameterFormInput.key
                          ]
                        return {
                          label: convertToMetersOrCentimeters(value as number, parameter?.unit),
                          value: +value,
                        }
                      })}
                      useTriangleTicks
                      minimalLabels
                      disabled={series.secondaryParameter.availableValues.length == 1}
                      useOnChangeCommitted
                    />
                  </Box>
                )}
                {series.secondaryParameterFormInput?.type == 'radioGroup' && (
                  <RadioGroup
                    css={radioGroupContent({ theme })}
                    value={series.secondaryParameter.value ?? 0}
                    key={`${series.secondaryParameterFormInput.key}-radioGroup`}
                    onChange={(event) => series.handleSecondaryParameterChange(+event.target.value)}
                    options={series.secondaryParameter.availableValues.sort().map((value) => ({
                      label: formatValue(series.secondaryParameterFormInput?.key, value),
                      value: +value,
                      selected: series.secondaryParameter?.value == value,
                    }))}
                  />
                )}
              </>
            )}
            {series.secondaryParameter.availableValues?.length == 1 && (
              <Typography sx={{ marginLeft: theme.spacing(2), fontSize: '12px', color: '#0b2948' }}>
                {`• ${formatValue(
                  series.secondaryParameterFormInput?.key,
                  series.secondaryParameter.availableValues[0],
                )}`}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {belowHeaderExtra}

      {series.hasError && (
        <Box css={chartSection({ theme })}>
          <EmptySection title="Data failed to load" />
        </Box>
      )}
      {!series.hasError && series.projectedRiskData.length > 0 && series.primaryParameterKey && (
        <Box css={chartSection({ theme })}>
          <Box
            css={css`
              ${chartContainer()}
              min-height: 200px;
            `}
          >
            <Box sx={{ width: '100%' }}>
              {series.isLoading ? (
                <EmptySection isLoading={true} title="Loading..." height={'200px'} />
              ) : (
                <ProjectedRiskChart
                  assetId={assetLayer.id}
                  yAxisTitle={yAxisTitle}
                  yAxisUnit={assetLayer.unit}
                  data={series.projectedRiskData}
                  parameter={series.primaryParameterKey}
                  parameterUnit={series.parameterUnit}
                  vulnerabilityList={series.vulnerabilityList}
                  xAxisMode={isRiskCurvesBlock ? xAxisMode : 'ari'}
                />
              )}
            </Box>
          </Box>
          {series.hasMissingData && (
            <Box
              css={{
                marginTop: theme.spacing(2),
                display: 'flex',
                backgroundColor: theme.palette.grey[100],
                marginLeft: theme.spacing(6),
                marginRight: theme.spacing(12),
                padding: theme.spacing(1),
                gap: theme.spacing(1),
              }}
            >
              <Box
                css={{
                  position: 'relative',
                  width: '28px',
                  height: '28px',
                  flexShrink: 0,
                  top: '-2px',
                }}
              >
                <Circle
                  style={{
                    width: '100%',
                    height: '100%',
                    color: 'white',
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                  }}
                />
                <ErrorOutline
                  style={{
                    width: '100%',
                    height: '100%',
                    color: theme.palette.grey[600],
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '1.3',
                  padding: theme.spacing(1),
                }}
              >
                Results are incomplete as{' '}
                {identifyingInformationKey === 'population'
                  ? 'population data'
                  : 'replacement cost data'}{' '}
                is not available for some assets.
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {!series.hasError && !series.isLoading && series.projectedRiskData.length == 0 && (
        <Box css={chartSection({ theme })}>
          <EmptySection title={`No ${title} chart available for this combination`} />
        </Box>
      )}
    </>
  )
}

export const ProjectedRisk = ({
  assetLayer,
  hazardLayer,
  yAxisTitle,
  identifyingInformationKey,
  onlyShowTotal = false,
}: ProjectedRiskProps) => {
  const theme = useTheme()
  const setProjectedRiskChartState = useSetAtom(projectedRiskChartStateAtom)
  const setProjectedRiskChartData = useSetAtom(projectedRiskChartDataAtom)

  const layerData = useLayerDataFetcher()
  const transformedLayerData = layerDataHazardIdTransformer(layerData)

  const regionMasks = useAtomValue(regionMasksAtom)
  const drawAreas = useAtomValue(drawAreasAtom)

  const [xAxisMode, setXAxisMode] = useState<'ari' | 'aep'>('ari')
  const [projectedConsequenceView, setProjectedConsequenceView] = useState<'ari' | 'eac'>('ari')
  const [showRiskCurvesInterpret, setShowRiskCurvesInterpret] = useState(false)

  const hazardGroupedByHazardId = useMemo(
    () => (hazardLayer.hazard_id ? transformedLayerData?.[hazardLayer.hazard_id] : undefined),
    [hazardLayer.hazard_id, transformedLayerData],
  )

  const parameterList: string[] = useMemo(() => {
    //Gets a list of at most two parameters that have numerical values and sorted by priority

    const parameterListWithNumericalValues = Object.keys(
      hazardGroupedByHazardId?.availableParameterValues ?? {},
    ).filter((key) => {
      const values = hazardGroupedByHazardId?.availableParameterValues[key].filter(
        (val) => !isNaN(parseFloat(`${val}`)),
      )
      return values && values.length > 1
    })

    const topTwoNumericalParameters =
      hazardGroupedByHazardId?.form
        .filter((formInput) => parameterListWithNumericalValues.includes(formInput.key))
        .sort((a, b) => a.priority - b.priority)
        .splice(0, 2)
        .map((formInput) => formInput.key) ?? []

    return topTwoNumericalParameters
  }, [hazardGroupedByHazardId?.availableParameterValues, hazardGroupedByHazardId?.form])

  const ariParameterKey = parameterList.find((p) => p.includes('ari') || p.includes('aep'))
  const otherParameterKey = parameterList.find((p) => p !== ariParameterKey)

  const formatValue: FormatValueFn = useCallback(
    (key, value) => {
      if (!key || value == null) return ''

      const parameter = hazardGroupedByHazardId?.parameters[key]
      const type = parameter?.type
      if (!type || !parameter) return ''

      switch (type) {
        case 'number':
          return convertToMetersOrCentimeters(+value, parameter.unit)
        case 'keyValue': {
          const keyValues = hazardGroupedByHazardId?.parameters[key]?.keyValues ?? {}

          return keyValues[value] ?? ''
        }
      }

      return `${value}`
    },
    [hazardGroupedByHazardId],
  )

  const riskCurves = useProjectedRiskSeries({
    primaryParameterKey: ariParameterKey,
    secondaryParameterKey: otherParameterKey,
    hazardGroupedByHazardId,
    hazardLayer,
    assetLayer,
    regionMasks,
    drawAreas,
    identifyingInformationKey,
    onlyShowTotal,
  })

  const projectedRisk = useProjectedRiskSeries({
    primaryParameterKey: otherParameterKey,
    secondaryParameterKey: ariParameterKey,
    hazardGroupedByHazardId,
    hazardLayer,
    assetLayer,
    regionMasks,
    drawAreas,
    identifyingInformationKey,
    mode: projectedConsequenceView,
  })

  const eal = useMemo(() => {
    if (!ariParameterKey || riskCurves.projectedRiskData.length < 2) return null

    // Add AEP values converted from ARI if they don't exist.
    // AEP can be calculated from ARI using 1 - e^(-1/ari)
    // The stored data is saved as a percentage so it's also
    // multiplied by 100 to keep it consistent
    const dataPointsWithAep = riskCurves.projectedRiskData.map((value) =>
      ariParameterKey === 'ari'
        ? { ...value, aep: 100 * (1 - Math.exp(-1 / (value['ari'] as number))) }
        : value,
    )

    const sortedDataPoints = dataPointsWithAep.sort((a, b) => (b.aep as number) - (a.aep as number))

    // Calculate the area under the curve of AEP and the parameter for the EAC
    // using the trapezoidal rule
    let result = 0
    for (let i = 0; i < sortedDataPoints.length - 1; i++) {
      const aep0 = (sortedDataPoints[i].aep as number) / 100
      const aep1 = (sortedDataPoints[i + 1].aep as number) / 100
      result += ((sortedDataPoints[i].total + sortedDataPoints[i + 1].total) / 2) * (aep0 - aep1)
    }
    return result
  }, [ariParameterKey, riskCurves.projectedRiskData])

  // Dollar-denominated metrics (replacement_cost, damage_cost) get a currency-prefixed "Value" stat;
  // every other metric (quantity, population, ...) gets a unit-suffixed "Quantity" stat.
  const isValueMetric =
    identifyingInformationKey === 'replacement_cost' || identifyingInformationKey === 'damage_cost'
  const ealLabel = isValueMetric
    ? 'Expected Annual Exposed Value'
    : 'Expected Annual Exposed Quantity'

  const currencyPrefix = useMemo(() => {
    const match = /\(([^)]+)\)/.exec(yAxisTitle)
    return match ? match[1] : '$'
  }, [yAxisTitle])

  const formattedEal = useMemo(() => {
    if (eal === null) return null
    const num = roundForDisplay(eal, isValueMetric ? currencyPrefix : assetLayer.unit)
    return isValueMetric
      ? `${currencyPrefix}${num}/year`
      : `${num}${assetLayer.unit ? ` ${assetLayer.unit}` : ''}/year`
  }, [eal, isValueMetric, currencyPrefix, assetLayer.unit])

  useEffect(() => {
    if (!hazardLayer.hazard_id) return

    const riskCurvesSecondaryParameterValue = formatValue(
      riskCurves.secondaryParameterFormInput?.key,
      riskCurves.secondaryParameter?.value,
    )

    const projectedRiskSecondaryParameterValue = formatValue(
      projectedRisk.secondaryParameterFormInput?.key,
      projectedRisk.secondaryParameter?.value,
    )

    setProjectedRiskChartState((current) => ({
      ...current,
      [assetLayer.id]: {
        ...current[assetLayer.id],
        [hazardLayer.hazard_id!]: {
          assetId: assetLayer.id,
          yAxisTitle,
          data: riskCurves.projectedRiskData,
          consequenceByReturnPeriod: ariParameterKey
            ? {
                primaryParameterKey: ariParameterKey,
                parameterUnit: riskCurves.parameterUnit,
                parameterTitle: parameterKeyTitleConverter(ariParameterKey, layerData),
                secondaryParameterKey: otherParameterKey ?? null,
                secondaryParameterValue: riskCurvesSecondaryParameterValue,
                secondaryParameterTitle: riskCurves.secondaryParameterFormInput?.title ?? null,
                vulnerabilityList: riskCurves.vulnerabilityList,
                xAxisMode,
              }
            : undefined,
          projectedConsequence: otherParameterKey
            ? {
                primaryParameterKey: otherParameterKey,
                parameterUnit: projectedRisk.parameterUnit,
                parameterTitle: parameterKeyTitleConverter(otherParameterKey, layerData),
                secondaryParameterKey: ariParameterKey ?? null,
                secondaryParameterValue: projectedRiskSecondaryParameterValue,
                secondaryParameterTitle: projectedRisk.secondaryParameterFormInput?.title ?? null,
                vulnerabilityList: projectedRisk.vulnerabilityList,
                projectedConsequenceView,
              }
            : undefined,
        },
      },
    }))

    setProjectedRiskChartData((state) => ({
      ...state,
      [assetLayer.id]: {
        ...state[assetLayer.id],
        [hazardLayer.hazard_id as string]: {
          riskCurves:
            ariParameterKey && riskCurves.projectedRiskData.length > 0
              ? {
                  data: riskCurves.projectedRiskData,
                  parameter: ariParameterKey,
                  vulnerabilityList: riskCurves.vulnerabilityList,
                  parameterTitle: parameterKeyTitleConverter(ariParameterKey, layerData),
                  parameterUnit: riskCurves.parameterUnit,
                  yAxisTitle,
                  xAxisMode,
                }
              : undefined,
          projectedConsequence:
            otherParameterKey && projectedRisk.projectedRiskData.length > 0
              ? {
                  data: projectedRisk.projectedRiskData,
                  parameter: otherParameterKey,
                  vulnerabilityList: projectedRisk.vulnerabilityList,
                  parameterTitle: parameterKeyTitleConverter(otherParameterKey, layerData),
                  parameterUnit: projectedRisk.parameterUnit,
                  yAxisTitle,
                  xAxisMode: 'ari',
                }
              : undefined,
        },
      },
    }))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assetLayer.id,
    formatValue,
    hazardGroupedByHazardId,
    hazardLayer.hazard_id,
    layerData,
    ariParameterKey,
    otherParameterKey,
    riskCurves.projectedRiskData,
    riskCurves.vulnerabilityList,
    riskCurves.parameterUnit,
    projectedRisk.projectedRiskData,
    projectedRisk.vulnerabilityList,
    projectedRisk.parameterUnit,
    yAxisTitle,
    xAxisMode,
    setProjectedRiskChartData,
    setProjectedRiskChartState,
  ])

  if (!parameterList.length) return null

  // Title/info copy for the Risk Curves block changes with the selected view.
  const riskCurveModeCopy =
    xAxisMode === 'aep'
      ? {
          title: 'Consequence Exceedance Curve',
          info: 'Shows the expected consequence for events with different return periods.',
          interpret: (
            <>
              <p>
                This curve shows the expected consequence against the probability of exceedance in
                any given year. Moving right represents more frequent events. A steeper curve
                indicates consequences increase rapidly as events become rarer, while a flatter
                curve suggests they change more gradually.
              </p>
            </>
          ) as ReactNode,
        }
      : {
          title: 'Consequence by Return Period',
          info: 'Shows the consequence expected for events with different average return periods.',
          interpret: (
            <p>
              This curve shows the expected consequence for increasingly rare events. Moving right
              represents less frequent events. A steeper curve indicates consequences increase
              rapidly as event rarity increases, while a flatter curve suggests they change more
              gradually. Use it to compare how consequences vary across design events.
            </p>
          ) as ReactNode,
        }

  return (
    <Box sx={{ margin: theme.spacing(1), minWidth: '400px', width: '100%' }}>
      {ariParameterKey && (
        <ProjectedRiskSeriesBlock
          theme={theme}
          title="Risk Curves"
          isRiskCurvesBlock
          series={riskCurves}
          hazardGroupedByHazardId={hazardGroupedByHazardId}
          assetLayer={assetLayer}
          yAxisTitle={yAxisTitle}
          xAxisMode={xAxisMode}
          identifyingInformationKey={identifyingInformationKey}
          formatValue={formatValue}
          headerExtra={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                paddingLeft: '32px',
                paddingRight: '32px',
                paddingTop: '8px',
                margin: '0px 0px 4px 0px',
              }}
            >
              <Box css={radioGroup({ theme })}>
                <Typography
                  sx={{ marginRight: theme.spacing(6), fontSize: '12px', color: '#0b2948' }}
                >
                  View
                </Typography>
              </Box>
              <ToggleButtonGroup
                css={toggleGroup({ theme })}
                value={xAxisMode}
                exclusive
                size="small"
                onChange={(_, value) => value && setXAxisMode(value)}
              >
                <ToggleButton value="ari">
                  <Typography sx={{ fontSize: '12px', color: '#0b2948' }}>
                    Return period (ARI)
                  </Typography>
                </ToggleButton>
                <ToggleButton value="aep">
                  <Typography sx={{ fontSize: '12px', color: '#0b2948' }}>
                    Exceedance probability (AEP)
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          }
          modeExtra={
            <Box
              sx={{
                paddingLeft: '48px',
                paddingRight: '32px',
                paddingTop: '4px',
                paddingBottom: '4px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '6px',
                }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px' }}
                >
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0b2948' }}>
                    {riskCurveModeCopy.title}
                  </Typography>
                  <Tooltip title={riskCurveModeCopy.info} placement="right">
                    <InfoOutlined
                      sx={{ fontSize: '14px', color: '#0b2948', opacity: 0.6, cursor: 'help' }}
                    />
                  </Tooltip>
                </Box>
                <InterpretToggle
                  active={showRiskCurvesInterpret}
                  onClick={() => setShowRiskCurvesInterpret((v) => !v)}
                />
              </Box>
              {showRiskCurvesInterpret && (
                <Box sx={{ marginTop: '8px' }}>
                  <InterpretPanel>{riskCurveModeCopy.interpret}</InterpretPanel>
                </Box>
              )}
            </Box>
          }
          belowHeaderExtra={
            formattedEal && (
              <Box sx={{ paddingLeft: '48px', paddingTop: '12px', paddingBottom: '8px' }}>
                <Typography sx={{ fontSize: '12px', color: '#0b2948' }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {ealLabel}:
                  </Box>{' '}
                  {formattedEal}
                </Typography>
              </Box>
            )
          }
        />
      )}

      {otherParameterKey && (
        <Box sx={{ marginTop: theme.spacing(6) }}>
          <ProjectedRiskSeriesBlock
            theme={theme}
            title="Projected Consequence"
            titleDescription="Shows how the selected consequence changes across future conditions."
            titleInterpret={
              <p>
                This chart shows how the selected consequence changes under different future
                conditions for the selected return period or Expected Annual Consequence (EAC).
                Steeper increases indicate consequences become more sensitive as conditions change,
                while flatter sections suggest relatively little change across that range.
              </p>
            }
            isRiskCurvesBlock={false}
            series={projectedRisk}
            hazardGroupedByHazardId={hazardGroupedByHazardId}
            assetLayer={assetLayer}
            yAxisTitle={yAxisTitle}
            xAxisMode={xAxisMode}
            identifyingInformationKey={identifyingInformationKey}
            formatValue={formatValue}
            hideSecondaryParameterControl={projectedConsequenceView == 'eac'}
            headerExtra={
              identifyingInformationKey && ariParameterKey ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingLeft: '32px',
                    paddingRight: '32px',
                    paddingTop: '8px',
                    margin: '0px 0px 4px 0px',
                  }}
                >
                  <Box css={radioGroup({ theme })}>
                    <Typography
                      sx={{ marginRight: theme.spacing(6), fontSize: '12px', color: '#0b2948' }}
                    >
                      View
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    css={toggleGroup({ theme })}
                    value={projectedConsequenceView}
                    exclusive
                    size="small"
                    onChange={(_, value) => value && setProjectedConsequenceView(value)}
                  >
                    <ToggleButton value="ari">
                      <Typography sx={{ fontSize: '12px', color: '#0b2948' }}>
                        By scenario
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="eac">
                      <Typography sx={{ fontSize: '12px', color: '#0b2948' }}>
                        Expected annual
                      </Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : null
            }
          />
        </Box>
      )}
    </Box>
  )
}

export default ProjectedRisk
